/**
 * Tally occurrences of each SKU in a list into {sku, quantity} pairs, normalizing
 * every SKU to a string. Shared by wallStorage (bundle SKU summaries) and
 * vehicleStorage (design SKU pricing lists), which independently reimplemented
 * the same count-then-pair logic.
 */
export const tallySkuQuantities = (skus) => {
  const counts = (Array.isArray(skus) ? skus : []).reduce((accumulator, sku) => {
    const normalizedSku = String(sku);
    accumulator.set(normalizedSku, (accumulator.get(normalizedSku) || 0) + 1);
    return accumulator;
  }, new Map());

  return Array.from(counts.entries()).map(([sku, quantity]) => ({ sku, quantity }));
};

/**
 * Classify each bundleInstanceId group as "single-SKU" (every member shares one
 * identical sku — a single physical unit split into multiple placed/rendered images,
 * e.g. a Wall Rail + End Cap - Left/Right kit) versus "mixed-SKU" (spans distinct
 * skus — an inseparable soft bundle: 2 brackets + 1 shelf, each its own real sku).
 * Mixed-SKU groups must never collapse, or all-but-one of their members would
 * silently disappear from whichever summary/pricing/sku-listing consumes this.
 *
 * Shared by api/design/utils.js's collectBundleSkus (raw WallProducts, before
 * placement) and getPlacedProductSkuAndQuantity.js (placed products, after
 * placement), which independently reimplemented the same
 * Map<bundleInstanceId, Set<sku>> -> filter(size === 1) classification. Each caller
 * adapts its own field casing (Sku/BundleInstanceId vs sku/bundleInstanceId) into
 * this shape before calling.
 *
 * @param {Array<{ sku: string|number|null, bundleInstanceId: string|null }>} items
 * @returns {Set<string>} bundleInstanceIds whose members all share one identical sku
 */
export const classifySingleSkuBundleInstanceIds = (items) => {
  const bundleGroupSkus = new Map();

  (Array.isArray(items) ? items : []).forEach(({ sku, bundleInstanceId }) => {
    if (sku == null || !bundleInstanceId) {
      return;
    }
    if (!bundleGroupSkus.has(bundleInstanceId)) {
      bundleGroupSkus.set(bundleInstanceId, new Set());
    }
    bundleGroupSkus.get(bundleInstanceId).add(sku);
  });

  return new Set(
    Array.from(bundleGroupSkus.entries())
      .filter(([, skus]) => skus.size === 1)
      .map(([bundleInstanceId]) => bundleInstanceId)
  );
};
